import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Plus, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";

const emptyForm = { client_name: "", result: "", story: "", before_image: "", image: "" };

function ImageField({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const testId = label.toLowerCase().replace(/\s+/g, "-");

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="text-xs text-[#71717A] mb-1 block">{label}</label>
      <div className="flex items-center gap-3">
        <input data-testid={`transformation-${testId}-input`} placeholder="Image URL" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
        <label data-testid={`transformation-${testId}-upload-label`} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs px-4 py-2.5 rounded-full cursor-pointer transition-colors shrink-0">
          <UploadSimple size={14} /> {uploading ? "..." : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
      {value && <img src={value} alt={label} className="w-20 h-20 rounded-lg object-cover mt-2" />}
    </div>
  );
}

export default function TransformationsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/transformations").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ client_name: t.client_name, result: t.result, story: t.story || "", before_image: t.before_image, image: t.image });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await api.put(`/admin/transformations/${editingId}`, form);
      else await api.post("/admin/transformations", form);
      toast.success(editingId ? "Transformation updated" : "Transformation added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/transformations/${id}`);
      toast.success("Transformation removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const canSave = form.client_name && form.result && form.before_image && form.image;

  return (
    <div data-testid="transformations-manager">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#71717A]">Manage the before/after cards shown in the homepage Transformations gallery.</p>
        <Button data-testid="add-transformation-button" onClick={openCreate} className="bg-[#D4AF37] text-black hover:bg-white rounded-full shrink-0">
          <Plus size={16} /> Add Transformation
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} data-testid={`transformation-item-${t.id}`} className="bg-[#141414] border border-[#27272A] rounded-2xl p-5 flex gap-4">
            <div className="flex gap-1 shrink-0">
              <img src={t.before_image} alt="before" className="w-14 h-14 rounded-lg object-cover" />
              <img src={t.image} alt="after" className="w-14 h-14 rounded-lg object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">{t.client_name}</p>
              <p className="text-xs text-[#D4AF37] mb-1">{t.result}</p>
              <p className="text-xs text-[#71717A] line-clamp-2">{t.story}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button data-testid={`edit-transformation-${t.id}`} onClick={() => openEdit(t)} className="text-[#71717A] hover:text-white transition-colors">
                <PencilSimple size={16} />
              </button>
              <button data-testid={`delete-transformation-${t.id}`} onClick={() => remove(t.id)} className="text-[#71717A] hover:text-red-400 transition-colors">
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
        {!items.length && <p className="text-[#71717A]">No transformations yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="transformation-dialog" className="bg-[#141414] border-[#27272A] text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">{editingId ? "Edit Transformation" : "Add Transformation"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="transformation-client-name-input" placeholder="Client Name (e.g. Jordan T.)" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
            <input data-testid="transformation-result-input" placeholder="Result (e.g. -18 lbs in 12 weeks)" value={form.result} onChange={(e) => setForm((f) => ({ ...f, result: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
            <textarea data-testid="transformation-story-input" placeholder="Short story / caption" rows={2} value={form.story} onChange={(e) => setForm((f) => ({ ...f, story: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
            <ImageField label="Before Image" value={form.before_image} onChange={(v) => setForm((f) => ({ ...f, before_image: v }))} />
            <ImageField label="After Image" value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} />
            <Button data-testid="transformation-save-button" disabled={!canSave || saving} onClick={save} className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-full">
              {saving ? "Saving..." : editingId ? "Save Changes" : "Add Transformation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
